import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Plus, Trash2, Search, Book, Pencil } from "lucide-react";
import { handleGet, handleRemove } from "../../../controllers/controller.all";
import Pagination from "../../../components/ui/Pagination";
import SortableHeader from "../../../components/ui/SortableHeader";

export default function MatiereList() {
    const user = useSelector((state) => state.userReducer?.user);
    const token = user?.access_token || "";
    const ecoleId = user?.ecole?._id || user?.ecole;
    
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    // Pagination & Sorting state
    const [currentPage, setCurrentPage] = useState(1);
    const [sortKey, setSortKey] = useState("libelle");
    const [sortOrder, setSortOrder] = useState("asc");
    const itemsPerPage = 20;

    const fetchItems = async () => {
        setLoading(true);
        await handleGet(`/matiere/ecole/${ecoleId}?skip=0&limit=100000`, (res) => {
            if (res?.matieres) {
                const filtered = res.matieres.filter(i => i.ecole === ecoleId || i.ecole?._id === ecoleId);
                setItems(filtered);
            }
        }, token);
        setLoading(false);
    };

    useEffect(() => {
        if(token) fetchItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ecoleId, token]);
    
    const handleSort = (key, order) => {
        setSortKey(key);
        setSortOrder(order);
    };

    const handleDelete = async (id) => {
        await handleRemove(`/matiere/${id}`, fetchItems, token);
    };


    const filtered_items = items.filter(item => {
        const s = "";
        // Dynamic search across string values
        return Object.values(item).some(val => 
            typeof val === 'string' && val.toLowerCase().includes(s)
        ) || (item.nom && item.nom.toLowerCase().includes(s)) ||
             (item.libelle && item.libelle.toLowerCase().includes(s)) ||
             (item.titre && item.titre.toLowerCase().includes(s)) ||
             (item.prenom && item.prenom.toLowerCase().includes(s));
    });

    const sorted_items = [...filtered_items].sort((a, b) => {
        let valA = a[sortKey] || "";
        let valB = b[sortKey] || "";
        
        // Handle nested objects commonly used for sorting like a.classe.libelle
        if (typeof valA === 'object' && valA !== null) {
            valA = valA.libelle || valA.nom || valA.prenom || "";
        }
        if (typeof valB === 'object' && valB !== null) {
            valB = valB.libelle || valB.nom || valB.prenom || "";
        }

        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();

        if (valA < valB) return sortOrder === "asc" ? -1 : 1;
        if (valA > valB) return sortOrder === "asc" ? 1 : -1;
        return 0;
    });

    const paginated_items = sorted_items.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    return (

        <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Matières</h1>
                    <p className="text-sm text-gray-500 mt-1">Gérez les matières enseignées.</p>
                </div>
                <Link 
                    to="/matieres/create" 
                    className="inline-flex items-center gap-2 bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 rounded-lg transition"
                >
                    <Plus className="h-4 w-4" /> Nouvelle Matière
                </Link>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                    <div className="relative max-w-sm w-full text-gray-400">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5" />
                        <input 
                            type="text" 
                            placeholder="Rechercher..." 
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <SortableHeader label="Libellé de la matière" sortKey="libelle" currentSort={sortKey} currentOrder={sortOrder} onSort={handleSort} />
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="2" className="px-6 py-10 text-center text-sm text-gray-500">Chargement...</td>
                                </tr>
                            ) : paginated_items.length === 0 ? (
                                <tr>
                                    <td colSpan="2" className="px-6 py-10 text-center text-sm text-gray-500">Aucune matière trouvée.</td>
                                </tr>
                            ) : (
                                paginated_items.map((item) => (
                                    <tr key={item._id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                                                    <Book className="h-5 w-5" />
                                                </div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">{item.libelle}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <Link 
                                                to={`/matieres/update/${item._id}`}
                                                className="text-indigo-600 hover:text-indigo-800 bg-indigo-50 p-2 rounded-lg transition inline-block mr-2"
                                                title="Modifier"
                                            >
                                                <Pencil className="h-4 w-4" />
                                            </Link>
                                            <button 
                                                onClick={() => handleDelete(item._id)}
                                                className="text-red-500 hover:text-red-700 bg-red-50 p-2 rounded-lg transition"
                                                title="Supprimer"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {filtered_items.length > itemsPerPage && (
                    <Pagination 
                        currentPage={currentPage} 
                        totalItems={filtered_items.length} 
                        itemsPerPage={itemsPerPage} 
                        onPageChange={setCurrentPage} 
                    />
                )}
            </div>
        </div>
    );
}
